import axios, { AxiosError } from 'axios';
import * as React from 'react';
import { useContext, useEffect, useState } from 'react';
import { useMatch } from 'react-router-dom';
import { Divider } from '../../../components/common/divider';
import { Flex } from '../../../components/common/flex';
import { NotificationContext } from '../../../components/contexts/notification_context';
import { UserContext } from '../../../components/contexts/user_context';
import { useResize } from '../../../hooks/use_resize';
import { TypeBookAction } from '../../bookings/dto';
import { IApproveBookPageData, IApproveBookPageProps } from './approve_book_page.d';
import {
  ApproveBookFooter,
  BookActionPerformed,
  BookCanceled,
  BookInfo,
  ContactsInfo,
  PaymentInfo,
  ResidenceGroupCard,
  ServiceCard,
} from './ui';

export const ApproveBookPage: React.FC<IApproveBookPageProps> = ({
  pathSlug = 'approve',
  websiteType = 'mirturbaz',
}) => {
  const [data, setData] = useState<IApproveBookPageData>();

  const matchUrl = [pathSlug, ':token'];
  // Чтоб работало на локалке (lvh.me:3000, lvh.me:3000/sanatoriums, lvh.me:3000/ubook)
  if (websiteType != 'mirturbaz') matchUrl.splice(0, 0, websiteType);

  const {
    params: { token },
  } = useMatch(`/${matchUrl.join('/')}`);
  const userContext = useContext(UserContext);
  const { isExtraLargeScreen } = useResize();
  const notyContext = useContext(NotificationContext);
  const { isMobile } = useResize();

  const handleActionPerformed = (action: TypeBookAction, reasons: Array<string>) => {
    setData((prevState) => ({
      ...prevState,
      performed_action: action,
      reasons,
    }));
  };

  useEffect(() => {
    let viewedPage: string = 'approve';
    if (pathSlug == 'a') {
      viewedPage = 'approve_short';
    }

    axios
      .get(`/api/books/${token}/approve.json?viewed_page=${viewedPage}`)
      .then(({ data }: { data: IApproveBookPageData }) => {
        setData(data);

        if (!userContext.current_camp?.id) {
          userContext.setCurrentCamp(data.current_camp);
        }
        if (!userContext.camps?.length && data.camps?.length) {
          userContext.setUserCamps(data.camps);
        }
      })
      .catch((error: AxiosError) => {
        notyContext.addNotification({
          type: 'danger',
          title: 'Ошибка загрузки данных.',
          message:
            error.code == 'ERR_BAD_REQUEST' ? 'Не удалось найти заявку. Проверьте правильность ссылки.' : error.message,
        });
      });
  }, []);

  const renderExtranetLink = () => (
    <>
      <Divider marginVertical={0} />
      <Flex vertical className={'approve-book-section approve-book-section--extranet'}>
        <span>
          Для просмотра всех заявок
          {isExtraLargeScreen ? <br /> : ' '}
          перейдите в{' '}
          <a id='link' href='/admin/index?utm_medium=approve&utm_campaign=approve.booking'>
            Экстранет
          </a>
          .
        </span>
      </Flex>
    </>
  );

  if (!data) return;

  if (data.canceled)
    return (
      <Flex vertical className={'approve-book-page'}>
        <BookCanceled />
        <Flex flex={1} />
        <ContactsInfo responsibleName={data.book.responsible_name} responsiblePhone={data.book.responsible_phone} />
      </Flex>
    );

  if (data.performed_action)
    return (
      <Flex vertical className={'approve-book-page'}>
        <BookActionPerformed action={data.performed_action} reasons={data.reasons} />
        {!userContext.current_user.id ? renderExtranetLink() : <Flex flex={1} />}
        <ContactsInfo responsibleName={data.book.responsible_name} responsiblePhone={data.book.responsible_phone} />
      </Flex>
    );

  return (
    <>
      <div style={(isMobile ? {} : { display: 'flex', flexWrap: 'wrap', flex: 1 })}>
        <div
          style={{
            ...(isMobile ? {} : {
              flex: '0 0 58%',
              borderRight: '1px solid #00000014',
              overflow: 'hidden',
              position: 'relative',
            }),
          }}
        >

          {isMobile && (
            <>
              <BookInfo book={data.book}/>

              <Divider marginVertical={0}/>
            </>
          )}

          <Flex vertical className={'approve-book-section'}>
            <h2 className={'approve-book-section__title typography-h2'}>Размещение</h2>
            {data.residence_groups.map((group, index) => (
              <ResidenceGroupCard residenceGroup={group} key={index}/>
            ))}
          </Flex>

          <Divider marginVertical={0}/>

          {!!data.services?.length && (
            <>
              <Flex vertical className={'approve-book-section'}>
                <h2 className={'approve-book-section__title typography-h2'}>Услуги</h2>
                {data.services.map((service, index) => (
                  <ServiceCard service={service} key={index}/>
                ))}
              </Flex>
              <Divider marginVertical={0}/>
            </>
          )}

          <PaymentInfo paymentInfo={data.payment_info}/>
        </div>
        <div
          style={{
            ...(isMobile ? {} : {
              flex: '0 0 42%',
              overflow: 'hidden',
              position: 'relative',
            }),
          }}
        >

          {!isMobile && (
            <>
              <BookInfo book={data.book}/>

              <Divider marginVertical={0}/>
            </>
          )}

          {!userContext.current_user.id && renderExtranetLink()}

          <ContactsInfo responsibleName={data.book.responsible_name} responsiblePhone={data.book.responsible_phone}/>
        </div>
      </div>
      <div className={'approve-book-section approve-book-section--margin-bottom'}></div>

      <ApproveBookFooter
        correctionReasons={data.correction_reasons}
        cancellationReasons={data.cancellation_reasons.map((reason) => ({label: reason.text, value: reason.value}))}
        bookId={data.book.id}
        bookToken={token}
        onActionPerformed={handleActionPerformed}
      />
    </>
  );
};
